import maplibreStyles from 'maplibre-gl/dist/maplibre-gl.css'
import { createRoot } from 'react-dom/client'
import { createFromFetch } from 'react-server-dom-webpack/client'

import { fetchViewerLocation } from './api/viewer'
import useBaseUrl from './hooks/useBaseUrl'
// @TODO we can use import attribute syntax after upgrading to eslint v9
// import styles from './styles.css' with { type: 'css' }
import styles from './styles.css'
import { parseCoordinates } from './util/parseCoordinates'

class SearchResultsElement extends HTMLElement {
  constructor () {
    super()

    /** @type {ShadowRoot} */
    this.dom = null
    /** @type {import('react-dom/client').Root} */
    this.root = null
  }

  async connectedCallback () {
    // Create a shadow root for encapsulation
    this.dom = this.attachShadow({ mode: 'open' })
    this.dom.adoptedStyleSheets = [styles, maplibreStyles]

    // Listen for locate-me events from the map component
    this.addEventListener('mq:locate-me:success', this._handleLocateMe.bind(this))

    this.root = createRoot(this.dom)

    // Fetch IP-based location if location is missing or invalid (client-side fallback)
    if (!parseCoordinates(this.getAttribute('location'))) {
      const viewerLocation = await fetchViewerLocation()
      if (viewerLocation) {
        this.setAttribute('location', `${viewerLocation[0]},${viewerLocation[1]}`)
      }
    }

    this._rerender()
  }

  /**
   * Debounce re-renders to batch rapid attribute changes (e.g. form submission)
   * into a single RSC fetch. Without this, each setAttribute triggers a separate
   * fetch, and concurrent in-flight requests can cause the widget to disappear.
   */
  _rerender () {
    if (!this.root) return

    if (this._rerenderTimer) return
    this._rerenderTimer = Promise.resolve().then(() => {
      this._rerenderTimer = null
      this._doRender()
    })
  }

  _doRender () {
    if (!this.root) return

    /** @TODO add unit tests to exercise both missing location and `location=""` */
    const query = this.getAttribute('query')
    if (!query) return

    const locationAttr = this.getAttribute('location')
    const location = parseCoordinates(locationAttr)
    if (!location && locationAttr) {
      this.log('Ignoring invalid location attribute; using IP fallback', locationAttr)
    }

    const rscUrl = new URL('rsc', useBaseUrl())
    const tenant = this.getAttribute('tenant')
    if (tenant) {
      rscUrl.searchParams.set('hostname', tenant)
    }

    const body = {
      dark: this.hasAttribute('dark'),
      hasPreciseLocation: this.hasAttribute('has-precise-location'),
      forceIntent: this.hasAttribute('force-intent'),
      variant: this.getAttribute('variant') || 'widget',
      query,
    }
    if (location) {
      body.location = location
    }

    const responsePromise = fetch(rscUrl, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        Accept: 'text/x-component',
        'Content-Type': 'application/json',
      },
    })

    this.root.render(createFromFetch(responsePromise))
  }

  _handleLocateMe (event) {
    const { latitude, longitude } = event.detail

    // Update location attribute with precise coordinates
    this.setAttribute('location', `${longitude},${latitude}`)

    // Set has-precise-location flag
    this.setAttribute('has-precise-location', '')

    this.log('User location acquired:', { latitude, longitude })
  }

  disconnectedCallback () {
    if (!this.root) return

    this.removeEventListener('mq:locate-me:success', this._handleLocateMe)
    this.root.unmount()
    this.root = null
  }

  // Observe attributes for changes
  static get observedAttributes () {
    return ['dark', 'has-precise-location', 'force-intent', 'location', 'query', 'tenant', 'variant']
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (oldValue === newValue) return

    if (name === 'dark' && this.dom?.firstElementChild) {
      this.dom.firstElementChild.classList.toggle('dark', newValue !== null)
      return
    }

    this._rerender()
  }

  log (message, ...args) {
    console.log(
      '%c mapq.st %c',
      'background: #3FB760;color: #FFF;border-radius: 2px',
      '',
      message,
      ...args
    )
  }
}

customElements.define('mq-srp', SearchResultsElement)
